import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Tag,
  Progress,
  Card,
  Empty,
  Statistic,
  Typography,
  Button,
  Tooltip,
  Select,
  Input,
  Row,
  Col,
} from "antd";
import {
  CheckCircleOutlined,
  SolutionOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  LeftOutlined,
  PieChartOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import AdminHOC from "../../../components/shared/HOC/AdminHOC.jsx";

import { userStats } from "../../../service/UserService.js";

import "./UserHistory.css";

import * as XLSX from "xlsx";

import DashCard from "./components/DashCard.jsx";

import Back from "../../../assets/back-btn.svg";

import CustomButton from "../../../components/shared/button/CustomButton.jsx";
import totalEnrollment from "../../../assets/icons/dashboard/Total Enrollments.svg";
import totalCurriCulum from "../../../assets/icons/dashboard/TotalCurriculum.svg";
import totalGroup from "../../../assets/icons/dashboard/TotalGroups.svg";
import CustomInput from "../../../components/shared/Input/Custom-Input.jsx";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon.jsx";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import useUserReport from "../../../hooks/useUserReport";
import CustomBreadcrumb from "../../../components/shared/breadcrumbs/CustomBreadCrumb";
import { PIE_CHART_CONFIG } from "../../../utility/commonConstant";
import HistoryCard from "./components/HistoryCard.jsx";
import CircularProgressChart from "../../../components/shared/charts/CircularProgressChart.jsx";
import DeadlineTable from "../../../components/shared/table/DeadlinesTable.jsx";
import CustomLabelDropdown from "../../../components/shared/Input/Custom-Label-DropDown.jsx";
import CommonTable from "../../../components/shared/table/Table.jsx";

const { Option } = Select;
const { Text, Title } = Typography;
const { Search } = Input;
const formatDate = (val) => (val ? new Date(val).toLocaleDateString() : "-");

const UserHistory = ({ setLoading, setBreadCrumbs, setHeader }) => {
  const { id } = useParams();
  const location = useLocation();
  const userName = location.state?.name || "N/A";
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { data, setData, groupOptions, error, fetchUserReport } = useUserReport();
  const [pageSize, setPageSize] = useState(() =>
    window.innerHeight >= 1024 ? 11 : 10,
  );
  const [dashStatsData, setDashStatsData] = useState({
    enrollments: 0,
    groups: 0,
    bundles: 0,
    kpis: {
      completedOnTimeCourses: 0,
      lateCompletionCourses: 0,
      completionFailedCourses: 0,
      inProgressCourses: 0,
      notStartedCourses: 0,
      defaulterCourses: 0,
    },
  });

  useEffect(() => {
    const handleResize = () => {
      setPageSize(window.innerHeight >= 1024 ? 11 : 10);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const statsData = await userStats(id);

        setDashStatsData(statsData.data);
      } catch (error) {
        console.error("Error loading user data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, setLoading]);

  const handleExport = () => {
    const currentColumns = subType === "BUNDLE" ? bundleColumns : courseColumns;
    const currentData = expandedRowKey
      ? topLevelEnrollments.filter((e) => e.enrollmentId === expandedRowKey)
      : topLevelEnrollments;

    const exportData = currentData.map((row) => {
      const formattedRow = {};
      currentColumns.forEach((col) => {
        const key = col.dataIndex;
        const title = col.title;

        let value = row[key];

        if (key === "status") {
          const statusMap = {
            COMPLETED: "Completed",
            IN_PROGRESS: "In Progress",
            PENDING: "Pending",
          };
          value = statusMap[value] || value;
        } else if (key === "allContentAcknowledged") {
          value = value ? "Yes" : "No";
        } else if (key === "quizScorePercentage") {
          if (value === null || value === undefined) {
            value = "NA";
          } else {
            const num = Number(value);
            value = `${num % 1 === 0 ? num : num.toFixed(1)}%`;
          }
        } else if (col.render && typeof col.render === "function") {
          const rendered = col.render(value, row);
          value = typeof rendered === "string" ? rendered : String(value);
        }
        formattedRow[title] = typeof value === "string" ? value : String(value);
      });

      return formattedRow;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "User Report");

    const tableType =
      subType === "BUNDLE" ? "Curriculums" : "IndividualCourses";
    XLSX.writeFile(wb, `${data.fullName}_${tableType}.xlsx`);
  };

  const [filterType, setFilterType] = useState("ALL");
  const [subType, setSubType] = useState("ALL");
  const [selectedGroup, setSelectedGroup] = useState("ALL");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("ALL"); // New status filter state
  const [searchText, setSearchText] = useState("");
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [expandedCourseKey, setExpandedCourseKey] = useState(null);
  const [isSearchHovered, setIsSearchHovered] = useState(false);

  useEffect(() => {
    fetchUserReport(id);
  }, [id]);

  if (!data) {
    return null;
  }


  const piedata = PIE_CHART_CONFIG.map((item) => ({
    ...item,
    number: dashStatsData.kpis?.[item.key] || 0,
  }));
  const filteredEnrollments = () => {
    if (!data) return [];

    return data.enrollments.filter((e) => {
      const isIndividual = ["INDIVIDUAL", "BUNDLE"].includes(
        e.enrollmentSource,
      );
      const isGroup = ["GROUP", "GROUP_BUNDLE"].includes(e.enrollmentSource);
      if (filterType === "GROUP" && !isGroup) return false;
      if (filterType === "INDIVIDUAL" && !isIndividual) return false;
      if (selectedGroup !== "ALL" && e.groupName !== selectedGroup)
        return false;
      if (subType === "COURSE" && e.sourceType !== "COURSE") return false;
      if (subType === "BUNDLE" && e.sourceType !== "BUNDLE") return false;
      if (
        subType === "COURSE" &&
        selectedCourse &&
        e.courseTitle !== selectedCourse
      )
        return false;
      if (
        subType === "BUNDLE" &&
        selectedBundle &&
        e.bundleName !== selectedBundle
      )
        return false;
      if (
        searchText &&
        !(
          (e.courseTitle &&
            e.courseTitle.toLowerCase().includes(searchText.toLowerCase())) ||
          (e.bundleName &&
            e.bundleName.toLowerCase().includes(searchText.toLowerCase()))
        )
      )
        return false;
      return true;
    });
  };

  const uniqueCourses = data?.enrollments
    ? [
      ...new Set(
        data.enrollments
          .filter((e) => e.sourceType === "COURSE")
          .map((e) => e.courseTitle),
      ),
    ]
    : [];

  const uniqueBundles = data?.enrollments
    ? [
      ...new Set(
        data.enrollments.filter((e) => e.bundleId).map((e) => e.bundleName),
      ),
    ]
    : [];

  const getCommonColumns = () => [
    {
      title: "Assigned At",
      dataIndex: "assignedAt",
      align: "center",
      render: formatDate,
      sorter: (a, b) => new Date(a.assignedAt) - new Date(b.assignedAt),
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      align: "center",
      render: formatDate,
      sorter: (a, b) => new Date(a.deadline) - new Date(b.deadline),
    },
    {
      title: "Progress",
      align: "center",
      dataIndex: "courseCompletionPercentage",
      render: (val) => {
        const num = Number(val || 0);
        return `${num % 1 === 0 ? num : num.toFixed(2)}%`;
      },
      sorter: (a, b) =>
        a.courseCompletionPercentage - b.courseCompletionPercentage,
    },
    {
      title: <div style={{ textAlign: "center" }}>Status</div>,
      align: "center",
      dataIndex: "status",
      render: (val) => {
        const statusStyles = {
          "In Progress": {
            color: "rgba(2, 93, 222, 1)",
            background: "rgba(232, 241, 255, 1)",
            borderColor: "rgba(139, 187, 255, 1)",
          },
          Completed: {
            color: "rgba(8, 138, 21, 1)",
            background: "rgba(238, 255, 240, 1)",
            borderColor: "rgba(149, 222, 157, 1)",
          },
          "Not Started": {
            color: "rgba(200, 0, 0, 1)",
            background: "rgba(255, 238, 238, 1)",
            borderColor: "rgba(255, 170, 170, 1)",
          },
          default: {
            color: "gray",
            background: "rgba(240, 240, 240, 1)",
            borderColor: "rgba(200, 200, 200, 1)",
          },
        };

        const style = statusStyles[val] || statusStyles.default;

        return (
          <Tag
            style={{
              padding: "10px 16px",
              borderRadius: "10px",
              border: `1px solid ${style.borderColor}`,
              color: style.color,
              backgroundColor: style.background,
            }}
          >
            {val}
          </Tag>
        );
      },
    },
    {
      title: "Adherence",
      dataIndex: "adherence",
      align: "center",
      sorter: (a, b) => (a.adherence || "").localeCompare(b.adherence || ""),
    },
    {
      title: "Acknowledged",
      align: "center",
      dataIndex: "allContentAcknowledged",
      render: (val) => (val ? "Yes" : "No"),
    },
    {
      title: "Quiz Score",
      align: "center",
      dataIndex: "quizScorePercentage",
      render: (val) => {
        if (val == null) return "NA";
        // Handle decimal formatting - show whole numbers without decimals
        const num = Number(val);
        return `${num % 1 === 0 ? num : num.toFixed(2)}%`;
      },
      sorter: (a, b) =>
        (a.quizScorePercentage || 0) - (b.quizScorePercentage || 0),
    },
  ];

  const courseColumns = [
    {
      title: "Title",
      width: "210px",
      dataIndex: "displayTitle",
      sorter: (a, b) => a.displayTitle.localeCompare(b.displayTitle),
      render: (_, record) => (
        <>
          <span className="enrollment-title">
            {record.displayTitle}
          </span>
          <div className="enrollment-meta">
            Enrollment Type: {record.enrollmentType}
            {record.groupName &&
              record.groupName !== "null" &&
              ["GROUP", "GROUP_BUNDLE"].includes(record.enrollmentSource) && (
                <div>Group: {record.groupName}</div>
              )}
            {record.bundleName &&
              record.bundleName !== "null" &&
              record.type === "Course" &&
              expandedRowKey === null && (
                <div>Curriculum: {record.bundleName}</div>
              )}
          </div>
        </>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      filters: [
        { text: "Course", value: "Course" },
        { text: "Bundle", value: "Bundle" },
      ],
      onFilter: (value, record) => record.type === value,
    },
    ...getCommonColumns(),
  ];

  const bundleColumns = [
    {
      title: "Curriculum Title",
      dataIndex: "bundleName",
      sorter: (a, b) => a.bundleName.localeCompare(b.bundleName),
    },
    ...getCommonColumns(),
  ];

  const contentColumns = [
    {
      title: "Title",
      dataIndex: "contentTitle",
    },
    {
      title: "Type",
      dataIndex: "contentType",
    },
    {
      title: "Completion",
      dataIndex: "contentCompletionPercentage",
      render: (val) => {
        const num = Number(val || 0);
        return `${num % 1 === 0 ? num : num.toFixed(2)}%`;
      },
    },
    {
      title: "Acknowledged",
      dataIndex: "acknowledgement",
      render: (val) => (val ? "Yes" : "No"),
      sorter: (a, b) =>
        (a.acknowledgement ? 1 : 0) - (b.acknowledgement ? 1 : 0),
    },
  ];

  // if (!data || data.enrollments.length === 0) {
  //   return (
  //     <div className="admin-header">
  //       <Empty
  //         image={Empty.PRESENTED_IMAGE_SIMPLE}
  //         description="No enrollments found"
  //         className="empty-table"
  //       />
  //     </div>
  //   );
  // }

  const bundleGroups = data.enrollments.reduce((acc, e) => {
    if (e.sourceType === "BUNDLE") {
      if (!acc[e.bundleId]) acc[e.bundleId] = [];
      acc[e.bundleId].push(e);
    }
    return acc;
  }, {});

  const topLevelEnrollments = filteredEnrollments()
    .filter((e, idx, arr) => {
      if (e.sourceType === "BUNDLE") {
        return arr.findIndex((x) => x.bundleId === e.bundleId) === idx;
      }
      return true;
    })
    .map((e) => {
      if (e.sourceType === "BUNDLE") {
        const courses = bundleGroups[e.bundleId] || [];

        const progressValues = courses.map(
          (c) => c.courseCompletionPercentage || 0,
        );
        const avgProgress = progressValues.length
          ? Math.round(
            progressValues.reduce((a, b) => a + b, 0) / progressValues.length,
          )
          : 0;

        const statuses = courses.map((c) => c.status);
        const isCompleted = statuses.every((s) => s === "Completed");
        const bundleStatus = isCompleted
          ? "Completed"
          : statuses.includes("In Progress")
            ? "In Progress"
            : "Not Started";

        const adherenceOrder = [
          "Overdue",
          "Not Due Yet",
          "Behind Schedule",
          "On Track",
          "Late",
          "On Time",
        ];
        const worstAdherence = courses.reduce((worst, c) => {
          const currentIndex = adherenceOrder.indexOf(
            c.adherence || "Completed",
          );
          const worstIndex = adherenceOrder.indexOf(worst || "Completed");
          return currentIndex > worstIndex ? c.adherence : worst;
        }, "Completed");

        const quizScores = courses.map((c) => {
          const score = c.quizScorePercentage;
          return score !== null && score !== undefined ? score : 0;
        });

        const avgQuizScore =
          quizScores.length > 0
            ? Math.round(
              quizScores.reduce((a, b) => a + b, 0) / quizScores.length,
            )
            : 0;

        // Calculate bundle acknowledgement: all courses must have all their content acknowledged
        const allCoursesAcknowledged =
          courses.length > 0
            ? (() => {
              const coursesWithContent = courses.filter(
                (course) =>
                  course.contentProgress && course.contentProgress.length > 0,
              );

              // If no courses have content, consider as not acknowledged
              if (coursesWithContent.length === 0) {
                return false;
              }

              // All courses with content must have all their content acknowledged
              return coursesWithContent.every((course) =>
                course.contentProgress.every(
                  (content) => content.acknowledgement === true,
                ),
              );
            })()
            : false;

        return {
          ...e,
          displayTitle: e.bundleName,
          enrollmentType: e.enrollmentSource || "INDIVIDUAL",
          type: "Bundle",
          courseCompletionPercentage: avgProgress,
          status: bundleStatus,
          adherence: worstAdherence,
          allContentAcknowledged: allCoursesAcknowledged,
          quizScorePercentage: avgQuizScore,
        };
      }

      // For individual courses, check if all content is acknowledged
      const allContentAcknowledged =
        e.contentProgress && e.contentProgress.length > 0
          ? e.contentProgress.every(
            (content) => content.acknowledgement === true,
          )
          : false;

      return {
        ...e,
        displayTitle: e.courseTitle,
        enrollmentType: e.enrollmentSource || "INDIVIDUAL",
        type: "Course",
        allContentAcknowledged,
        quizScorePercentage: e.quizScorePercentage,
      };
    })
    .filter((e) => {
      // Apply status filter to the processed enrollments
      if (selectedStatus !== "ALL" && e.status !== selectedStatus) return false;
      return true;
    });

  const dashData = [
    {
      id: 1,
      header: "Total Enrollments",
      input: dashStatsData.enrollments,
      icon: totalEnrollment,
    },
    {
      id: 2,
      header: "Total Groups",
      input: dashStatsData.groups,
      icon: totalGroup,
    },
    {
      id: 3,
      header: "Total Curriculums",
      input: dashStatsData.bundles,
      icon: totalCurriCulum,
    },
  ];


  // if (!data || data.enrollments.length === 0) {
  //   return (
  //     <div style={{ padding: "0px 16px" }}>
  //       <Text>No enrollments found.</Text>
  //     </div>
  //   );
  // }

  return (
    <div className="admin-container ">
      <div>
        <div className="admin-header">
          <span className="admin-page-title">{userName} Details</span>

          <div className="export-button">
            <CustomButton
              icon={<DownloadOutlined />}
              onClick={handleExport}
              style={{ marginRight: "10px" }}
            >
              Download
            </CustomButton>
            <CustomButton
              icon={<QuestionCircleOutlined />}
              onClick={() => {
                navigate(
                  `/quiz-report?userId=${id}&userName=${encodeURIComponent(
                    data.fullName || userName,
                  )}`,
                );
              }}
            >
              Quiz Reports
            </CustomButton>
          </div>
        </div>
      </div>

      <div className="dash-data">
        <div style={{
          display: "flex",
          gap: "10px"
        }}>
          {dashData.map((item, index) => (
            <HistoryCard
              key={index}
              header={item.header}
              value={item.input}
              icon={item.icon}
            />
          ))}
        </div>
        <div>
          <CircularProgressChart data={piedata} />
        </div>

        <div
          style={{
            height: "34vh",
            overflowY: "scroll",
            width: "100%",
          }}
        >
          <DeadlineTable userId={id} />
        </div>

        {/* <div className='table-subtitle'>
        User course progress history
      </div> */}
      </div>
      <div className="admin-table-container">
        <div className="search-div">
          <div
            onMouseEnter={() => setIsSearchHovered(true)}
            onMouseLeave={() => setIsSearchHovered(false)}
          >
            <CustomInput
              icon={
                isSearchHovered ? (
                  <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                ) : (
                  <img
                    src={searchIconsvg}
                    alt="search"
                    style={{ width: 24, height: 24 }}
                  />
                )
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search Courses or Curriculums"
              allowClear
            />
          </div>
          <div
            style={{
              width: "18vw",
            }}
          >
            <CustomLabelDropdown
              value={filterType}
              onChange={(v) => {
                setFilterType(v);
                setSelectedGroup("ALL");
              }}
              options={[
                { value: "ALL", label: "All Enrollments" },
                { value: "GROUP", label: "Group" },
                { value: "INDIVIDUAL", label: "Individual" },
              ]}
              wrapperClass="filter-type-selector"
            />
          </div>

          {filterType === "GROUP" && (
            <div>
              <CustomLabelDropdown
                value={selectedGroup}
                onChange={setSelectedGroup}
                options={[
                  { value: "ALL", label: "All Groups" },
                  ...groupOptions.map((g) => ({ value: g, label: g })),
                ]}
                placeholder="Select group"
                wrapperClass="group-selector"
                style={{ width: "100%" }}
              />
            </div>
          )}
          <div>
            <CustomLabelDropdown
              value={subType}
              onChange={(v) => {
                setSubType(v);
                setSelectedCourse(null);
                setSelectedBundle(null);
              }}
              options={[
                { value: "ALL", label: "All Types" },
                { value: "COURSE", label: "Individual Courses" },
                { value: "BUNDLE", label: "Curriculums" },
              ]}
              placeholder="Select type"
              style={{ width: 180 }}
              wrapperClass="subtype-selector"
            />
          </div>

          {subType === "COURSE" && (
            <div>
              <CustomLabelDropdown
                value={selectedCourse || "ALL"}
                onChange={(v) => setSelectedCourse(v === "ALL" ? null : v)}
                options={[
                  { value: "ALL", label: "All Courses" },
                  ...uniqueCourses.map((c) => ({ value: c, label: c })),
                ]}
                placeholder="Select course"
                wrapperClass="course-selector"
                style={{ width: "100%" }}
              />
            </div>
          )}
          {subType === "BUNDLE" && (
            <div>
              <CustomLabelDropdown
                value={selectedBundle || "ALL"}
                onChange={(v) => setSelectedBundle(v === "ALL" ? null : v)}
                options={[
                  { value: "ALL", label: "All Curriculums" },
                  ...uniqueBundles.map((b) => ({ value: b, label: b })),
                ]}
                placeholder="Select curriculum"
                wrapperClass="bundle-selector"
                style={{ width: "100%" }}
              />
            </div>
          )}
          <div>
            <CustomLabelDropdown
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={[
                { value: "ALL", label: "All Status" },
                { value: "Completed", label: "Completed" },
                { value: "In Progress", label: "In Progress" },
                { value: "Not Started", label: "Not Started" },
              ]}
              placeholder="Select status"
              wrapperClass="status-selector"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        {/* {topLevelEnrollments.length === 0 ? (
          <Text
            style={{ display: "block", textAlign: "center", marginTop: 24 }}
          >
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Text>
        ) : ( */}
        <CommonTable
          columns={subType === "BUNDLE" ? bundleColumns : courseColumns}
          data={
            expandedRowKey
              ? topLevelEnrollments.filter(
                (e) => e.enrollmentId === expandedRowKey,
              )
              : topLevelEnrollments
          }
          rowKey="enrollmentId"
          pagination={{ pageSize: 10 }}
          expandedRowRender={(record) => {
            // If this is an individual course enrollment, show its contentProgress
            if (record.sourceType === "COURSE") {
              return record.contentProgress?.length > 0 ? (
                <CommonTable
                  data={record.contentProgress}
                  columns={contentColumns}
                  pagination={false}
                  rowKey="contentId"
                />
              ) : (
                "No content started."
              );
            }

            // If this is a bundle, show the bundle's courses (with their own expandable content)
            if (record.type === "Bundle") {
              const bundleCourses = bundleGroups[record.bundleId] || [];

              const prepareCourseRows = (courses) =>
                courses.map((c) => {
                  const allContentAcknowledged =
                    c.contentProgress && c.contentProgress.length > 0
                      ? c.contentProgress.every(
                        (content) => content.acknowledgement === true,
                      )
                      : false;

                  return {
                    ...c,
                    displayTitle: c.courseTitle,
                    enrollmentType: c.enrollmentSource || "INDIVIDUAL",
                    type: "Course",
                    hasContent: c.contentProgress?.length > 0,
                    allContentAcknowledged,
                  };
                });

              const rows = expandedCourseKey
                ? prepareCourseRows(bundleCourses).filter(
                  (c) => c.enrollmentId === expandedCourseKey,
                )
                : prepareCourseRows(bundleCourses);

              return bundleCourses.length > 0 ? (
                <CommonTable
                  data={rows}
                  columns={courseColumns}
                  pagination={false}
                  rowKey="enrollmentId"
                  expandedRowRender={(innerRecord) => {
                    if (innerRecord.contentProgress?.length > 0) {
                      return (
                        <CommonTable
                          data={innerRecord.contentProgress}
                          columns={contentColumns}
                          pagination={false}
                          rowKey="contentId"
                        />
                      );
                    }
                    return <Text type="secondary">No content started.</Text>;
                  }}
                  onExpand={(expanded, innerRecord) =>
                    setExpandedCourseKey(
                      expanded ? innerRecord.enrollmentId : null,
                    )
                  }
                  rowExpandable={() => true}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              );
            }

            // return null;
          }}
          onExpand={(expanded, record) =>
            setExpandedRowKey(expanded ? record.enrollmentId : null)
          }
        />
        {/* )} */}
      </div>
    </div>
  );
};

export default AdminHOC(UserHistory);
